import { getActiveGoals, getGoalActual, getEffectiveGoals, isDayActive } from './goalTypes';
import { browserDayKey, shiftDayKey } from './datetime';

/**
 * Calculate the current streak (consecutive goal-met days) from daily stats.
 *
 * Each day is judged against the goals that were active on that day
 * (its `goal_snapshot`), falling back to the current goals for days
 * without one — same rule as the 7-day list and the calendar. Adding or
 * raising a goal today therefore doesn't retroactively break the streak.
 *
 * @param {Array}  dailyStats  - Array of daily stat objects.
 * @param {Object} dailyGoals  - Dict of active goals, e.g. { words: 30 }.
 * @param {string} [todayKey]  - User-day "today" key (``YYYY-MM-DD``) from
 *                               ``GET /user/day-window``. Falls back to the
 *                               browser's *local* calendar date if not
 *                               supplied — this can still drift from the
 *                               backend's user-day near the 03:00-local
 *                               rollover, so callers that can pass it in
 *                               should.
 * @returns {number} Current streak count.
 */
export const calcStreak = (dailyStats, dailyGoals, todayKey = null) => {
  if (!dailyStats || dailyStats.length === 0) return 0;

  const goalMetDates = new Set();
  for (const day of dailyStats) {
    const isActive = isDayActive(day);
    const activeGoals = getActiveGoals(getEffectiveGoals(day, dailyGoals));

    let goalMet;
    if (activeGoals.length === 0) {
      // No goals configured: any activity counts
      goalMet = true;
    } else {
      goalMet = activeGoals.every(
        (g) => getGoalActual(day, g.key) >= g.target,
      );
    }

    if (isActive && goalMet) {
      goalMetDates.add(day.date);
    }
  }

  const resolvedTodayKey = todayKey || browserDayKey();

  let count = 0;
  // Step backward by one calendar day, parsing YYYY-MM-DD without locale.
  const stepBack = (key) => shiftDayKey(key, -1);

  let cursor = resolvedTodayKey;
  // If today's goal isn't met yet, skip today and start from yesterday.
  if (!goalMetDates.has(cursor)) {
    cursor = stepBack(cursor);
  }
  while (goalMetDates.has(cursor)) {
    count++;
    cursor = stepBack(cursor);
  }

  return count;
};
